import React, {FC, memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
// @ts-ignore
import MuxPlayer from '@mux/mux-player-react'
import "@mux/mux-player/themes/minimal";
import {useStateValue} from "../../state-provider/StateProvider";
import './VideoPlayer.css'
import {log} from "../../../utils/log";
import {getVideoCurrentTime, getVideoMuted, setVideoCurrentTime, setVideoMuted} from "../../../utils/storage";
import {VideoPlayerPinnedMessagesOverlay} from "./VideoPlayerPinnedMessagesOverlay";
import {setVideoPlayerControlsVisible, setVideoPlayerSourceOrientation} from "../../app/App.action";
import * as StackBlur from 'stackblur-canvas';
import {VideoPlayerCenterPlayButton} from "./VideoPlayerCenterPlayButton";
import {MetricsType, ws} from "../../../events/ws";
import {useOrientation} from "../../../hooks/useOrientation";
import useIsMobile from "../../../hooks/useIsMobile";

const VideoPlayerComponent: FC<{
  liveStreamUrl: string | undefined,
  onDemandUrl: string | undefined,
}> = ({
  liveStreamUrl,
  onDemandUrl
}) => {


  const [{
    userActivation,
    header,
    sequencePollUuid,
    isWidgetMaximized,
    videoPlayerSourceOrientation,
  }, dispatch] = useStateValue()

  const playerRef = useRef<any>()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const [video, setVideo] = useState<HTMLVideoElement>()

  const orientation = useOrientation()
  const isLandscape = orientation === "landscape"

  const isMobile = useIsMobile()

  const [paused, setPaused] = useState<boolean>(true)
  const [skipVolumeChange, setSkipVolumeChange] = useState<boolean>(true)

  useEffect(() => {
    if (video && videoPlayerSourceOrientation) {
      video.onclick = onVideoPlayerClick
      if (!isWidgetMaximized) {
        video.style.objectFit = "cover"
      } else if (videoPlayerSourceOrientation === "Vertical" && !isLandscape) { // vertical videos only cover in portrait
        video.style.objectFit = "cover"
      } else if (videoPlayerSourceOrientation === "Horizontal" && !isMobile) {
        video.style.objectFit = "contain"
      } else if (videoPlayerSourceOrientation === "Horizontal" && isMobile && isLandscape) {
        video.style.objectFit = "contain"
      } else if (videoPlayerSourceOrientation === "Horizontal") {
        video.style.objectFit = "cover"
      } else {
        video.style.objectFit = "contain"
      }
    }
  }, [video, isWidgetMaximized, videoPlayerSourceOrientation, isMobile, isLandscape])

  useEffect(() => {
    if (liveStreamUrl && video) {
      ws.clearMetrics(MetricsType.videoPaused)
      ws.clearMetrics(MetricsType.videoPlaying)
      ws.registerMetrics(MetricsType.liveStreamPlaying, () => {
        return true
      })
      ws.registerMetrics(MetricsType.videoMuted, () => {
        return video.volume === 0.0
      })
      ws.registerMetrics(MetricsType.videoUnmuted, () => {
        return video.volume !== 0.0
      })
    } else if (onDemandUrl && video) {
      ws.clearMetrics(MetricsType.liveStreamPlaying)
      ws.registerMetrics(MetricsType.videoPlaying, () => {
        return !video.paused
      })
      ws.registerMetrics(MetricsType.videoPaused, () => {
        return video.paused
      })
      ws.registerMetrics(MetricsType.videoMuted, () => {
        return video.muted
      })
      ws.registerMetrics(MetricsType.videoUnmuted, () => {
        return !video.muted
      })
    }
  }, [liveStreamUrl, onDemandUrl, video])


  // Effect to initialize video element and capture background
  useEffect(() => {

    if (video) {
      return
    }

    const interval = setInterval(() => {

      if (playerRef?.current && sequencePollUuid && canvasRef.current && playerRef.current.videoWidth !== 0 && playerRef.current.videoHeight !== 0) {
        let orientation: "Horizontal" | "Vertical" = "Horizontal"
        if (playerRef.current.videoWidth < playerRef.current.videoHeight) {
          orientation = "Vertical"
        }
        dispatch(setVideoPlayerSourceOrientation({ videoPlayerSourceOrientation: orientation }))

        try {
          let v = playerRef.current.shadowRoot.querySelector("mux-video").shadowRoot.querySelector("video") as HTMLVideoElement
          setVideo(v)

          // Poll until we can actually draw a frame from the video
          const pollForFrame = () => {
            const checkInterval = setInterval(() => {
              if (v.readyState >= 2 && v.currentTime > 0) {
                clearInterval(checkInterval)
                capture(v)
              }
            }, 50)

            // Timeout after 3 seconds
            setTimeout(() => clearInterval(checkInterval), 3000)
          }

          pollForFrame()
        } catch (e) {
          log("failure to retrieve mux video element", e)
        }

        clearInterval(interval)
      }
    }, 10)

    return () => clearInterval(interval)
  }, [playerRef, liveStreamUrl, onDemandUrl, video, header, canvasRef])

  // Separate effect to handle initial autoplay attempt
  useEffect(() => {
    if (!video || !sequencePollUuid || !playerRef.current) {
      return
    }

    let userMuted = getVideoMuted(sequencePollUuid)
    setSkipVolumeChange(true)

    // Try playing without user activation first
    log(`try playing because user activation didn't happen, user muted = ${userMuted}`)
    playerRef.current.muted = userMuted
    playerRef.current.play().then(() => {
      setPaused(false)
    }).catch(() => {
      log(`playing with user muted = ${userMuted} failed, trying again with user muted = true`)
      playerRef.current!!.muted = true
      playerRef.current!!.play().then(() => {
        setPaused(false)
      }).catch(() => {
        // can't do anything else, ensure paused state is set correctly
        log(`playing with user muted = true failed, setting paused state to show play button`)
        setPaused(true)
        // Force load the video metadata so it's ready to play on user interaction
        if (video && video.readyState < 1) {
          video.load()
        }
      })
    })

    setTimeout(() => {
      setSkipVolumeChange(false)
    }, 100)
  }, [video, sequencePollUuid])

  // Separate effect to handle playback when userActivation becomes true
  useEffect(() => {
    if (!userActivation || !video || !sequencePollUuid || !playerRef.current) {
      return
    }

    let userMuted = getVideoMuted(sequencePollUuid)
    setSkipVolumeChange(true)

    // Only try to play if video is currently paused
    if (playerRef.current.paused) {
      log(`try playing because user activation happened, user muted = ${userMuted}`)
      playerRef.current.muted = userMuted
      playerRef.current.play().then(() => {
        setPaused(false)
      }).catch(() => {
        log(`playing with user activation failed, setting paused state`)
        setPaused(true)
      })
    } else {
      // Video is already playing (likely auto-started muted), update mute state
      log(`video already playing after user activation, updating muted state to ${userMuted}`)
      playerRef.current.muted = userMuted
    }

    setTimeout(() => {
      setSkipVolumeChange(false)
    }, 100)
  }, [userActivation, video, sequencePollUuid])

  useEffect(() => {
    let interval = setInterval(() => {
      if (playerRef?.current && onDemandUrl) {
        setVideoCurrentTime(onDemandUrl, playerRef.current.currentTime)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [playerRef, onDemandUrl])

  useEffect(() => {
    let interval = setInterval(() => {
      if (playerRef?.current) {
        dispatch(setVideoPlayerControlsVisible({ videoPlayerControlsVisible: true }))//(!playerRef.current.mediaController.hasAttribute("userinactive") || playerRef.current.paused) }))
      }
    }, 100)
    return () => clearInterval(interval)
  }, [playerRef, liveStreamUrl])

  const muxPlayerStyles = useMemo(() => {
    let styles: any = {
      position: "absolute",
      "--seek-backward-button": "none",
      "--seek-forward-button": "none",
      "--playback-rate-button": "none",
      "--fullscreen-button": "none",
      "--pip-button": "none",
      "--live-button": "none",
      "--time-display": "none",
      "--bottom-play-button": "none",
      "overflow": "hidden",
      "width": "100%",
      "height": "100%",
    }

    if (liveStreamUrl) {
      styles = {
        ...styles,
        "--time-range": "none",
      }
    }

    if (isWidgetMaximized) {
      styles = {
        ...styles,
        height: "100%",
      }
    } else {
        styles = {
          ...styles,
          "--controls": "none",
          objectFit: "cover",
        }
    }

    // todo bring back middle play button

    if (isMobile) {
      styles = {
        ...styles,
        "--volume-range": "none",
      }
    }

    return styles
  }, [isWidgetMaximized, isLandscape, isMobile, liveStreamUrl, onDemandUrl, videoPlayerSourceOrientation])

  useEffect(() => {
    document.getElementById("tiny-theme")?.remove()
    const template = document.createElement("template")
    template.id = "tiny-theme";
    template.innerHTML = `
    <media-controller>
      <slot name="media" slot="media"></slot>
      <media-control-bar part="bottom control-bar">
        <media-play-button></media-play-button>
        <media-mute-button part="bottom mute button"></media-mute-button>
        <media-volume-range part="bottom volume range"></media-volume-range>
        <media-time-range part="bottom time range"></media-time-range>
      </media-control-bar>
    </media-controller>
  `
    document.body.appendChild(template)
  }, [])

  function capture(video: HTMLVideoElement) {
    let scaleFactor = 1
    var w = video.videoWidth * scaleFactor
    var h = video.videoHeight * scaleFactor
    if (w === 0 || h === 0)
      return

    if (canvasRef.current) {

      if (canvasRef.current.width !== w) {
        canvasRef.current.width = w
      }
      if (canvasRef.current.height !== h) {
        canvasRef.current.height = h
      }
      var ctx = canvasRef.current.getContext('2d')
      if (ctx) {
        ctx.drawImage(video, 0, 0, w, h)
        StackBlur.canvasRGB(canvasRef.current, 0, 0, w, h, 100)
        const base64Image = canvasRef.current.toDataURL("image/png")
        video.style.background = `url("${base64Image}") center/cover no-repeat`
      }
    }
  }

  const onPause = useCallback(() => {
    setPaused(true)
    if (playerRef.current && liveStreamUrl) {
      playerRef.current.play()
    }
  }, [liveStreamUrl, playerRef])

  const onPlay = () => {
    setPaused(false)
  }

  const onVolumeChange = (e: any) => {
    if (skipVolumeChange) {

    } else {
      let status = playerRef.current.muted
      setVideoMuted(sequencePollUuid!!, status)
      log(`not skipping volume change: ${status} ${playerRef?.current?.volume}`)
      // setMuted(status)
    }
  }

  // const setPlayerMuted = useCallback((muted: boolean) => {
  //   playerRef.current.muted = muted
  // }, [playerRef])

  /**
   * By default mux-player in mobile only puases/plays on double-click, because control bar is also hidden. But given that we're always showing control bar, this also needs to be overridden
   */
  const onVideoPlayerClick = () => {
    if (playerRef.current.paused) {
      playerRef.current.play()
    } else {
      playerRef.current.pause()
    }
  }

  return (
    <>
      <canvas ref={canvasRef} id="videoPlayerBackgroundCanvas"></canvas>
      <MuxPlayer
        theme={"tiny-theme"}
        startTime={onDemandUrl ? getVideoCurrentTime(onDemandUrl) : undefined}
        style={muxPlayerStyles}
        preferPlayback={"mse"}
        onPause={onPause}
        onPlay={onPlay}
        onVolumeChange={onVolumeChange}
        ref={playerRef}
        loop={!!onDemandUrl}
        src={onDemandUrl ? onDemandUrl : undefined}
        playbackId={liveStreamUrl ? liveStreamUrl.replace("https://stream.mux.com/", "").split(".")[0] : undefined}
      />
      {/*<VideoPlayerMuteControl muted={muted} setMuted={setPlayerMuted}/>*/}
      <VideoPlayerPinnedMessagesOverlay liveStreamUrl={liveStreamUrl} />
      <VideoPlayerCenterPlayButton visible={paused} onClick={() => {
        if (playerRef.current) {
          if (playerRef.current.paused) {
              playerRef.current.play()
            } else {
              playerRef.current.pause()
            }
        }
      }}/>
    </>
  )
}

export const VideoPlayer = memo(VideoPlayerComponent)